/*
 * useSperrGui.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-11-05 in huWaWi3 33.4.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { IContextualMenuItemStyles, getTheme } from '@fluentui/react';
import gql from 'graphql-tag';
import * as React from 'react';
import toast from 'react-hot-toast';
import { AnyVariables, OperationResult, useMutation } from 'urql';

import { AUFTRAG_FRAGMENT } from '../../fragments';
import { ICommandBarItemProps } from '../../lib/muipanels';

const SPERR_MUTATION = gql`
  mutation SperrMutation($key: ID!, $oldRev: String, $zuteilungssperre: Boolean, $message: String) {
    result: updateHwAuftragBoolean(
      auftragKeyString: $key
      auftragRev: $oldRev
      fieldName: "zuteilungssperre"
      value: $zuteilungssperre
      message: $message
    ) {
      auftrag {
        ...auftragFragment
      }
      success
      message
      oldRev
      newRev
      duration
    }
  }
  ${AUFTRAG_FRAGMENT}
`;

export function useSperrGui(auftrag) {
  const theme = getTheme();
  const [disabled, setDisabled] = React.useState(false);
  const [, mutateEntity] = useMutation(SPERR_MUTATION);

  // Styles for both command bar and overflow/menu items
  const importantItemStyle: Partial<IContextualMenuItemStyles> = {
    icon: { color: theme.palette.red },
    iconHovered: { color: theme.palette.redDark },
  };

  const update = (sperren: boolean) => {
    setDisabled(true);
    const prom = async () => {
      const r = await mutateEntity({
        key: auftrag._keyStr,
        oldRev: auftrag._rev,
        zuteilungssperre: sperren,
        message: `Zuteilungssperre ${sperren ? 'gesetzt' : 'aufgehoben'}`,
      } as any);
      if (r.error) throw r.error;
      if (!r.data.result.success) throw new Error(r.data.result.message);
      return r;
    };
    toast.promise(prom(), {
      loading: 'Update Zuteilungssperre',
      success: (r: OperationResult<any, AnyVariables>) => {
        setDisabled(false);
        return (
          <React.Fragment>
            Erfolgreich die zuteilungssperre auf {JSON.stringify(r.data.result.auftrag.zuteilungssperre)}{' '}
            geändert.
          </React.Fragment>
        );
      },
      error: (e) => {
        setDisabled(false);
        console.error(e);
        return `Fehler: ${e}`;
      },
    });
  };

  // if (!auftrag || (auftrag.status > '600' || !auftrag.zuteilungssperre)) {
  //   return [];
  // }

  const item: ICommandBarItemProps = auftrag.zuteilungssperre
    ? {
        key: 'entsperren',
        text: 'Zuteilungssperre aufheben',
        ariaLabel: 'Zuteilungssperre aufheben',
        iconOnly: false,
        iconProps: { iconName: 'Unlock' },
        buttonStyles: importantItemStyle,
        disabled: false,
        onClick: () => update(false),
      }
    : {
        key: 'sperren',
        text: 'Zuteilungssperre setzen',
        title: 'Zuteilungssperre setzen',
        ariaLabel: 'Zuteilungssperre setzen',
        iconOnly: false,
        iconProps: { iconName: 'Lock' },
        disabled,
        onClick: () => update(true),
      };
  return [item];
}
